import { Country, Location } from 'api/references/types'
import { ListItemType } from 'api/types'

export type GetVehiclesListResponse = {
  vehicles: ListItemType<Vehicle>
}

export type Vehicle = {
  id: string
  name: string
  groupName: string
  uniqueId: string
  sippCode: string
  image: string
  selected: boolean
  companyClass?: string
  hirePoints?: number[]
  hasEmptyFields?: boolean
}

export type GetStopSalesListResponse = {
  stopSells: ListItemType<StopSales>
}

type StopSales = {
  id: string
  country: string
  city: string
  location: string
  carClass: string
  blockType: StopSalesBlockType
  from: string
  to: string
  applicable: StopSalesRange
  created: string
}

export type StopSalesAdd = {
  id?: number
  countryId?: number
  contryName?: string
  from?: string
  to?: string
  applicable?: StopSalesRange
  comments?: string
  companyID?: number
  cities: number[] | null
  locations: number[] | null
  countries: number[] | null
  carClasses: string[] | null
}

export type StopSaleEditDto = {
  id: number
  carClasses: string[]
  countryName: string
  countryId: number
  cities: string[]
  locations: string[]
  countries: string[]
  from: string
  to: string
  applicable: StopSalesRange
  created: string
}

export type FullCompanyStopSalesDto = {
  id?: number
  countryId?: number
  countryName?: string
  cities?: Country[]
  locations?: Location[]
  from?: string
  to?: string
  carClasses?: string[]
  applicable?: StopSalesRange
  comments?: string
  created?: string
  blockType?: StopSalesBlockType
}

export enum StopSalesRange {
  Both = 'Both',
  PickUp = 'PickUp',
  DropOff = 'DropOff',
}

export enum StopSalesBlockType {
  Permanent = 'Permanent',
  Temporary = 'Temporary',
}

export type AddVehicleRequest = {
  carId?: number | null
  sippCode?: string
  companyClass?: string
  carCategoryId?: number
  transmission?: string
  airConditioning?: string
  fuel?: string
  doors?: string
  sits?: string
  driverAgeRange?: DriverAgeRange
  hirePoints?: number[]
  carCategoryName?: string
  carType?: string
  carModelName?: string
  carModelImageContent?: string
  quantity?: number
}

export type FullVehicle = {
  vehicleInfo: AddVehicleRequest & {
    id?: string
    carName?: string
    carCategoryName?: string
  }
}

export enum Transmission {
  Automatic = 'Automatic',
  Manual = 'Manual',
}

export enum AirConditioning {
  NA = 'N/A',
  Yes = 'Yes',
  No = 'No',
}

export enum Fuel {
  Unspecified = 'Unspecified',
  Petrol = 'Petrol',
  Diesel = 'Diesel',
  Hybrid = 'Hybrid',
  Electric = 'Electric',
  Gas = 'Gas',
  Hydrogen = 'Hydrogen',
  MultiFuel = 'MultiFuel',
  Ethanol = 'Ethanol',
}

export type DriverAgeRange = {
  rangeType: DriverAgeRangeType
  items: DriverAgeRangeItem[]
}

export enum DriverAgeRangeType {
  PerRental = 'PerRental',
  PerDay = 'PerDay',
  NoFee = 'NoFee',
}

export type DriverAgeRangeItem = {
  ageStart: string
  ageEnd: string
  price: string
  maxPrice: string
}

type Equipment = {
  hasEmptyFields?: boolean
  id: number
  groupId: string
  uniqueId: string
  name: string
  maxCount: number
  isAvailable: boolean
  count: number
  sum: number
  maxSum: number
  refersToLocation: string
  applyTo: string
}

export type GetEquipmentsListResponse = {
  extras: ListItemType<Equipment>
}

export type SeasonRange = {
  start: string
  end: string
  description?: string
  seasonRangeId?: number
  uniqueId?: string
  rateCode: string
}

export type DailyRange = {
  rangeStart: string
  rangeEnd: string
  rangeType: 'Fixed' | 'Daily'
  dailyRangeId?: number
  description?: string
  uniqueId?: string
}

export type HirePoint = {
  id: string
  name: string
}

export type FullCompanyEquipmentDto = {
  id?: number
  extraId?: string
  extraName?: string
  seasonRanges?: SeasonRange[]
  priceFor?: PaymentPeriod
  price?: string
  maxAmount?: string
  maxCount?: number
  hirePoints?: HirePoint[]
  isRequired?: boolean
}

export type EquipmentAdd = Omit<FullCompanyEquipmentDto, 'id' | 'extraName'>

export enum PaymentPeriod {
  Rent = 1,
  Day = 2,
}

export type PriceListResponse = {
  priceList: ListItemType<PriceList>
}

export type PriceList = {
  id: number
  country: string
  location: string
  seasonRange: string
  dailyRange: string
  startPeriod: string
  endPeriod: string
  isEnabled: boolean
  countriesOfResidence: string
  hasEmptyFields?: boolean
}

export type FullPriceList = {
  id?: number
  isActive?: boolean
  year?: string
  seasonRanges?: SeasonRange[]
  dailyRanges?: DailyRange[]
  priceListType: PriceListType
  comment?: string
  hirePoints?: number[]
  countriesOfResidence?: string[]
}

export type PriceListAdd = Omit<
  FullPriceList,
  'id' | 'rates' | 'seasonRanges'
> & {
  companyID: number
  seasonRanges: SeasonRangeAdd[]
}

export type SeasonRangeAdd = {
  start: {
    day: number
    month: number
  }
  end: {
    day: number
    month: number
  }
  seasonRangeId?: number
}

export type AddOrEditPriceList = {
  id?: number
  isActive?: boolean
  year?: string
  dailyRanges?: DailyRange[]
  priceListType: PriceListType
  comment?: string
  hirePoints?: number[]
  rates?: []
  seasonRanges: SeasonRangeAdd[]
}

export enum PriceListType {
  MapByCompanyClass = 'MapByCompanyClass',
  MapBySipp = 'MapBySIPP',
}

export type CarClassItem = {
  carClass: string
  carName: string
}

export type PriceListRates = {
  availableCarClasses: CarClassItem[]
  seasonRangeDescription?: string
  countriesOfResidence?: string
  dailyRangeDescription?: string
  priceListType?: PriceListType
  comment?: string
  country?: string
  location?: string
  year?: string
  cars: ListItemType<PriceListCar>
  seasonRanges: Required<SeasonRange>[]
  dailyRanges: Required<DailyRange>[]
}

export type PriceListCar = {
  carClass: string
  carType: string
  carName: string
  rates: PriceListRate[]
}

export type PriceListRate = {
  rateId: number
  seasonRangeId: number
  dailyRangeId: number
  price: number
  rateCode: string
}

export type PriceListVehicle = {
  class: string
  model: string
  sipp: string
}

export type PriceListRateEdit = {
  seasonRangeId: number
  carClass: string
  price: number
  dailyRangeId: number
}

export type VehicleInfo = {
  airConditioning: AirConditioning
  transmission: Transmission
  fuel: Fuel
  sits: string
  doors: string
  carCategoryId: string
}

export type CarsFilters = {
  carModel: string[]
  companyClasses: string[]
  sipp: string[]
  carCategories: string[]
  refersToLocations: string[]
}

export type EquipmentsFilters = {
  equipment: string[]
}
